/**
 * LeadChannelsCard — preferred + current contact channel selectors.
 *
 * Writes via PATCH /leads/{id}. Backend emits `lead.contact_channel_updated`
 * when either field changes.
 *
 * `LeadChannelRows` is the chrome-less variant (rows only) so other cards
 * (e.g. LeadPreferences' "Perfil del comprador") can embed the same
 * selectors without duplicating the patch logic or gluing two cards.
 */
import React from "react";
import { api, formatError } from "@/lib/api";
import { WhatsappLogo, EnvelopeSimple, Phone, InstagramLogo, ChatCircle } from "@phosphor-icons/react";

const CHANNELS = [
  { key: "whatsapp",  label: "WhatsApp",  icon: WhatsappLogo },
  { key: "email",     label: "Email",     icon: EnvelopeSimple },
  { key: "call",      label: "Llamada",   icon: Phone },
  { key: "instagram", label: "Instagram", icon: InstagramLogo },
  { key: "other",     label: "Otro",      icon: ChatCircle },
];

/** `showCurrent=false` hides the "Canal actual" row — operational state,
 * not a buyer preference. The standalone card keeps both rows. */
export function LeadChannelRows({ lead, onChanged, showCurrent = true }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const patch = async (payload) => {
    setBusy(true); setError("");
    try {
      await api.patch(`/leads/${lead.lead_id}`, payload);
      onChanged && onChanged();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const renderRow = (title, field, current) => (
    <div>
      <div className="text-xs text-stone-500 mb-1">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          const active = current === c.key;
          return (
            <button
              key={c.key}
              type="button"
              disabled={busy}
              onClick={() => patch({ [field]: active ? null : c.key })}
              data-testid={`lead-channel-${field}-${c.key}`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs transition-all ${
                active
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
              }`}
              title={c.label}
            >
              <Icon size={12} /> {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {error && (
        <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700">{error}</div>
      )}
      {renderRow("Canal preferido", "preferred_contact_channel", lead.preferred_contact_channel)}
      {showCurrent && renderRow("Canal actual", "current_conversation_channel", lead.current_conversation_channel)}
    </>
  );
}

export default function LeadChannelsCard({ lead, onChanged }) {
  return (
    <div className="dv-card space-y-3" data-testid="lead-channels-card">
      <h3 className="text-sm font-medium text-stone-800">Canales de contacto</h3>
      <LeadChannelRows lead={lead} onChanged={onChanged} />
    </div>
  );
}
